/**
 * Подсорт: чистая половина обоих экранов — «Расчёта» и «Задач».
 *
 * Здесь нет ни запроса, ни разметки: только правила, по которым площадка, три
 * числа её полки и отмеченные строки превращаются в отбор, в фразу под
 * заголовком и в тело кнопки «Создать». Поэтому всё это проверяется тестом без
 * браузера — тем же способом, каким живут `lib/select.ts` и `lib/places.ts`.
 */

/** Значение одного отбора. Три, а не флаг: «любой» — это ответ. */
export type Choice = "any" | "yes" | "no";

export const CHOICES: readonly { value: Choice; label: string }[] = [
  { value: "any", label: "любой" },
  { value: "yes", label: "есть" },
  { value: "no", label: "нет" },
];

/**
 * Площадка — ровно ОДНА (задача владельца 17.09.26: «две сразу нельзя
 * выбрать»). Умолчание — ВБ, и в адресе оно не пишется: ссылка `/fbw?city=7`,
 * разосланная до переименования раздела, обязана открыться той же.
 */
export type Platform = "wb" | "ozon";

export const PLATFORMS: readonly {
  value: Platform;
  /** Кнопка переключателя. */
  label: string;
  /** Чья полка — в подписях: «на складе ВБ». */
  shelf: string;
}[] = [
  { value: "wb", label: "ВБ", shelf: "ВБ" },
  { value: "ozon", label: "Ozon", shelf: "Ozon" },
];

/** Площадка из адреса. Чужое слово — ВБ, так же, как на сервере. */
export function platformOf(value: string | null | undefined): Platform {
  return (value ?? "").trim().toLowerCase() === "ozon" ? "ozon" : "wb";
}

/** Как зовётся полка площадки в подписях. */
export function shelfName(platform: Platform): string {
  return PLATFORMS.find((p) => p.value === platform)?.shelf ?? "ВБ";
}

/**
 * Три числа полки — три отбора, и подписаны они тем, чем эти числа являются.
 *
 * `amount` — что лежит на складе площадки и продаётся; `to_client` уехало
 * покупателю и не вернётся; `from_client` едет обратно возвратом. У ВБ и у
 * Ozon меры одни и те же (`repo/ozon_stock.py`), различается только имя полки.
 */
export function fields(platform: Platform): readonly {
  key: "shelf" | "to_client" | "from_client";
  label: string;
  title: string;
}[] {
  const name = shelfName(platform);
  return [
    {
      key: "shelf",
      label: `на складе ${name}`,
      title: `Сколько лежит на складе ${name} и продаётся прямо сейчас`,
    },
    {
      key: "to_client",
      label: "едет клиенту",
      title: "Уже уехало покупателю: на полку не вернётся, но спрос доказывает",
    },
    {
      key: "from_client",
      label: "возврат",
      title: "Едет обратно от покупателя: дыру закроет, но не завтра",
    },
  ];
}

/** Чужое слово в адресе читается как «любой» — так же, как на сервере. */
export function choice(value: string | null | undefined): Choice {
  const text = (value ?? "").trim().toLowerCase();
  return text === "yes" || text === "no" || text === "any" ? (text as Choice) : "any";
}

/**
 * Отбор, когда ключа в адресе НЕТ. «На складе» и «возврат» по умолчанию «нет»
 * (владелец 11.09.26), поэтому их «любой» обязан лечь в адрес словом `any`:
 * пустой ключ вернул бы умолчание, и «любой» не нажимался бы вовсе (разметка
 * владельца 23.09.26).
 */
export const CHOICE_DEFAULT = { shelf: "no", to: "any", from: "no" } as const;

/** Что писать в адрес: умолчание не пишется, остальное — словом. */
export function choiceParam(key: keyof typeof CHOICE_DEFAULT, value: Choice): string {
  return value === CHOICE_DEFAULT[key] ? "" : value;
}

/**
 * Чем отобран список — одной фразой под заголовком.
 *
 * «Любой» в фразу не идёт — он ничего не сужает, и перечислять его значило бы
 * удлинять строку молчанием.
 */
export function filterWords(
  filter: { shelf: string; to_client: string; from_client: string },
  platform: Platform = "wb",
): string {
  const name = shelfName(platform);
  const said: string[] = [];
  const shelf = choice(filter.shelf);
  if (shelf === "no") said.push(`на складе ${name} пусто`);
  if (shelf === "yes") said.push(`на складе ${name} есть`);
  const to = choice(filter.to_client);
  if (to === "no") said.push("клиенту не едет");
  if (to === "yes") said.push("едет клиенту");
  const from = choice(filter.from_client);
  if (from === "no") said.push("возврата нет");
  if (from === "yes") said.push("едет возврат");
  return said.length ? said.join(" · ") : "любое состояние полки";
}

/** Полка строкой таблицы: ноль — прочерком, три цифры подряд читаются глазами. */
export function shelfNumber(value: number): string {
  return value > 0 ? String(value) : "—";
}

/**
 * Заказы недели под баркодом: «12 заказов за 7 дней».
 *
 * `null` — дневного среза кабинета нет вовсе, и «0 заказов» здесь соврало бы в
 * самую дорогую сторону. Ноль — измеренный ответ.
 */
export function ordersWords(orders: number | null, days: number): string {
  if (orders === null) return "заказы неизвестны";
  const tail = orders % 10;
  const hundred = orders % 100;
  const word =
    tail === 1 && hundred !== 11
      ? "заказ"
      : tail >= 2 && tail <= 4 && (hundred < 10 || hundred >= 20)
        ? "заказа"
        : "заказов";
  return `${orders} ${word} за ${days} дн`;
}

/**
 * Чем сужен остаток — одной фразой рядом с отбором полки.
 *
 * Бронь FBS и задачи подсорта — чужие обещания (вещь лежит в коробе, но её
 * уже ждут), запас — наше решение. Неучтённое говорится вслух: молчание
 * читалось бы как «всё свободно», а подсорт увёз бы вещь из-под задачи.
 */
export function stockWords(
  keep: number,
  reserveKnown: boolean,
  tasksKnown = true,
): string {
  const said: string[] = [];
  said.push(keep > 0 ? `оставляем в коробах ${keep} шт` : "увозим хоть последнюю");
  said.push(reserveKnown ? "бронь FBS учтена" : "бронь FBS НЕ учтена");
  said.push(tasksKnown ? "задачи подсорта учтены" : "задачи подсорта НЕ учтены");
  return said.join(" · ");
}

/** Отмеченная строка расчёта — ровно то, что нужно кнопке «Создать». */
export type CreateSource = { card: { card_id: number } | null; need: number };

/** Позиция тела `POST /api/podsort/tasks`. */
export type CreateItem = { card_id: number; count: number };

/**
 * Что уедет в задачи: по позиции на строку, числом «Собрать».
 *
 * Строка без карточки или с нулём не едет вовсе: задачу соседа без товара не
 * поставить, а ноль штук — не задача. Число берётся ИЗ СТРОКИ, а не
 * пересчитывается здесь: сервер сверит его своим расчётом и откажет целиком,
 * если оно устарело.
 */
export function createItems(rows: readonly CreateSource[]): CreateItem[] {
  const seen = new Set<number>();
  const out: CreateItem[] = [];
  for (const row of rows) {
    const id = row.card?.card_id ?? 0;
    if (!id || row.need <= 0 || seen.has(id)) continue;
    seen.add(id);
    out.push({ card_id: id, count: row.need });
  }
  return out;
}

/** Сколько штук уедет в задачи. */
export function createUnits(items: readonly CreateItem[]): number {
  return items.reduce((n, one) => n + one.count, 0);
}

/**
 * Почему «Создать» сейчас нельзя — или пустая строка, если можно.
 *
 * ГОРОД ОБЯЗАТЕЛЕН: задача соседа живёт в городе, и «весь склад» задачей быть
 * не может — позиция из двух городов досталась бы обоим сборщикам сразу.
 * Причина говорится словами на кнопке, а не прячется за погашенной кнопкой.
 */
export function createBlocked(cityId: string, items: readonly CreateItem[]): string {
  if (!cityId) return "Выберите город: задача подсорта ставится в городе";
  if (items.length === 0) return "Отметьте позиции галочками";
  return "";
}

/** Отказ двери «Создать» — словами для человека. */
export function createRefusal(code: string): string {
  switch (code) {
    case "need_changed":
      return "Расчёт изменился, пока список был открыт: ничего не создано. Список перечитан — проверьте числа и создайте снова.";
    case "busy":
      return "В этом городе прямо сейчас создают задачи: ничего не создано. Попробуйте через несколько секунд.";
    case "fbs_write_denied":
      return "Нет права записи в задачи FBS: ничего не создано. Грант ставит инфраструктура.";
    case "fbs_db_disabled":
      return "База задач FBS не настроена в этой полосе: создавать некуда.";
    case "fbs_unavailable":
      return "База задач FBS не отвечает: ничего не создано.";
    case "crm_unavailable":
      return "CRM не отвечает: пересчитать остаток нечем, ничего не создано.";
    case "unauthorized":
      return "Сессия истекла: войдите заново.";
    default:
      return `Не создано: ${code}`;
  }
}

/** Адрес списка задач, суженного той же площадкой и городом. */
export function tasksHref(platform: Platform, cityId: string | number): string {
  const q = new URLSearchParams();
  q.set("platform", platform);
  if (cityId && String(cityId) !== "0") q.set("city", String(cityId));
  return `/podsort/tasks?${q.toString()}`;
}

/**
 * Отбор статуса списка задач. «Открытые» — не статус, а вопрос смены «что
 * осталось сделать»: вещь ещё в коробе, задача ждёт или уже в руках сборщика.
 */
export const TASK_STATUS_ALL = "all";
export const TASK_STATUS_OPEN = "open";

/** Цвет статуса задачи: что ещё впереди, что сделано, что снято. */
export function statusTone(code: string): "wait" | "work" | "done" | "off" {
  if (code === "new") return "wait";
  if (code === "in_progress") return "work";
  if (code === "picked") return "done";
  return "off";
}
